<div class="cp-wrap">
  <!-- HERO -->
  <header class="cp-hero" role="banner">
    <div class="cp-hero-glow" aria-hidden="true"></div>
    <div class="cp-eyebrow">
      <span aria-hidden="true">🚫</span> Cancellation Policy
    </div>
    <h1 class="cp-title">
      Cancellation <span class="text-accent">Policy</span>
    </h1>
    <p class="cp-sub">
      This policy explains how subscription cancellations work on
      <strong>Angularize.dev</strong>, operated by
      <strong>Pythrust Technologies OPC Private Limited</strong>. You may cancel
      at any time — but all payments are final and non-refundable without
      exception.
    </p>
    <div class="cp-meta">
      <div class="meta-pill"><strong>Effective:</strong> {{ lastUpdated }}</div>
      <div class="meta-pill">
        <strong>Owner:</strong> Pythrust Technologies OPC Pvt. Ltd.
      </div>
      <div class="meta-pill">
        <strong>Jurisdiction:</strong> Gurugram, Haryana, India
      </div>
      <div class="meta-pill">
        Also see:
        <a routerLink="/terms-and-conditions">Terms</a> ·
        <a routerLink="/privacy-policy">Privacy</a> ·
        <a routerLink="/refund-policy">Refunds</a>
      </div>
    </div>
  </header>

  <!-- BODY -->
  <div class="cp-layout">
    <!-- SIDEBAR TOC -->
    <aside class="toc" aria-label="Table of Contents">
      <p class="toc-label">Contents</p>
      <ul class="toc-list">
        <li *ngFor="let s of tocSections">
          <a
            [class.active]="s.active"
            (click)="scrollTo(s.id)"
            (keydown.enter)="scrollTo(s.id)"
            tabindex="0"
            [attr.aria-current]="s.active ? 'location' : null"
            >{{ s.label }}</a
          >
        </li>
      </ul>
    </aside>

    <!-- MAIN CONTENT -->
    <main class="cp-content" role="main">
      <!-- ── C1: INTRODUCTION ── -->
      <section class="sec" id="c1">
        <span class="sec-num">Section 01</span>
        <h2 class="sec-heading">Introduction</h2>
        <p>
          This Cancellation Policy ("<strong>Policy</strong>") governs all
          subscription cancellations for <strong>Angularize.dev</strong>, a SaaS
          platform operated by
          <strong>Pythrust Technologies OPC Private Limited</strong>
          ("<strong>Company</strong>", "<strong>we</strong>",
          "<strong>us</strong>", or "<strong>our</strong>").
        </p>
        <p>
          By subscribing to any paid plan on <strong>angularize.dev</strong> or
          <strong>beta.angularize.dev</strong>, you acknowledge that you have
          read, understood, and agreed to this Policy in full. This Policy must
          be read alongside our
          <a routerLink="/terms-and-conditions">Terms of Service</a>,
          <a routerLink="/privacy-policy">Privacy Policy</a>, and
          <a routerLink="/refund-policy">Refund Policy</a>, all of which are
          incorporated herein by reference.
        </p>

        <!-- THE CENTREPIECE BLOCK -->
        <div class="absolute-rule">
          <div class="rule-icon" aria-hidden="true">⚖️</div>
          <div class="rule-body">
            <p class="rule-heading">The Absolute Rule</p>
            <p class="rule-text">
              You may cancel your subscription at any time.<br />
              <strong>No money will be refunded — ever.</strong><br />
              This applies to all plan types, all billing cycles, and all
              circumstances without exception.
            </p>
          </div>
        </div>
      </section>

      <!-- ── C2: ABSOLUTE NO-REFUND ── -->
      <section class="sec" id="c2">
        <span class="sec-num">Section 02</span>
        <h2 class="sec-heading">The Absolute No-Refund Rule</h2>
        <p>
          Angularize.dev is a digital SaaS product. Upon subscription, access to
          the Platform — including its AI-powered code generation engine,
          Angular scaffolding tools, GCP infrastructure, and
          Firebase-authenticated user environment — is delivered immediately and
          continuously. The costs of providing this access are incurred by the
          Company from the moment your subscription begins, irrespective of
          whether you choose to use the Platform.
        </p>
        <p>
          Accordingly, the following rule applies
          <strong
            >absolutely, without exception, and without qualification</strong
          >:
        </p>

        <div class="rule-grid">
          <div class="rule-card">
            <div class="rule-card-icon">📅</div>
            <p class="rule-card-label">Monthly Plans</p>
            <p class="rule-card-text">
              Cancel at any time. Zero refund for the current month, regardless
              of when in the billing cycle you cancel.
            </p>
          </div>
          <div class="rule-card">
            <div class="rule-card-icon">📆</div>
            <p class="rule-card-label">Annual Plans</p>
            <p class="rule-card-text">
              Cancel at any time. Zero refund for any unused months remaining in
              the annual period, regardless of how early you cancel.
            </p>
          </div>
          <div class="rule-card">
            <div class="rule-card-icon">⚡</div>
            <p class="rule-card-label">Any Other Plan</p>
            <p class="rule-card-text">
              Lifetime, enterprise, or custom plans — all carry the same
              absolute no-refund rule upon cancellation.
            </p>
          </div>
        </div>

        <p>
          The following circumstances do <strong>not</strong> create an
          exception to this rule and will not be considered as grounds for any
          refund:
        </p>
        <ul class="doc-list">
          <li>
            You did not use the Platform, or used it less than expected, during
            the billing period;
          </li>
          <li>
            You cancel on the first day of the billing period, one day after
            subscription, or at any other early point;
          </li>
          <li>
            You were dissatisfied with the quality of AI-generated code or LLM
            outputs;
          </li>
          <li>
            You found an alternative tool or decided not to continue with
            Angular development;
          </li>
          <li>
            You were unaware of or did not read this Policy before subscribing;
          </li>
          <li>Your business circumstances changed after subscribing;</li>
          <li>
            A specific feature you expected was not available at the time of
            cancellation;
          </li>
          <li>
            You experienced temporary platform degradation or downtime that did
            not constitute a total outage;
          </li>
          <li>
            You cancel an annual plan mid-year, regardless of how many months
            remain;
          </li>
          <li>
            You cancel a monthly plan after partial use, regardless of how few
            days have elapsed;
          </li>
          <li>
            A team member, employee, or authorised user subscribed on your
            behalf without your knowledge;
          </li>
          <li>
            You believe the pricing was not clearly communicated at point of
            purchase.
          </li>
        </ul>

        <div class="callout callout-warn">
          <strong>⚠ No Exceptions, No Special Cases</strong>
          This is not a policy with hidden exceptions or case-by-case discretion
          for refunds on cancellation. No customer service representative,
          employee, or agent of Pythrust Technologies OPC Private Limited has
          authority to override this rule. Any promise of a refund upon
          cancellation made outside of this written Policy is not binding on the
          Company.
        </div>
      </section>

      <!-- ── C3: HOW TO CANCEL ── -->
      <section class="sec" id="c3">
        <span class="sec-num">Section 03</span>
        <h2 class="sec-heading">How to Cancel Your Subscription</h2>
        <p>
          We make cancellation straightforward. You do not need to speak to
          anyone or provide a reason. Follow these steps:
        </p>

        <div class="steps-list">
          <div class="step">
            <div class="step-num">1</div>
            <div class="step-body">
              <p class="step-title">Log in to your account</p>
              <p class="step-desc">
                Go to <strong>angularize.dev</strong> and sign in with your
                registered credentials.
              </p>
            </div>
          </div>
          <div class="step">
            <div class="step-num">2</div>
            <div class="step-body">
              <p class="step-title">Open Billing Settings</p>
              <p class="step-desc">
                Navigate to your account menu and click
                <strong>Credits</strong>, then
                <strong>Manage your Subscriptions</strong>.
              </p>
            </div>
          </div>
          <div class="step">
            <div class="step-num">3</div>
            <div class="step-body">
              <p class="step-title">Access the billing portal</p>
              <p class="step-desc">
                Click <strong>Edit Billing</strong> to open the billing
                management portal.
              </p>
            </div>
          </div>
          <div class="step">
            <div class="step-num">4</div>
            <div class="step-body">
              <p class="step-title">Cancel your subscription</p>
              <p class="step-desc">
                Click <strong>Cancel Subscription</strong> and confirm your
                cancellation. You will receive a confirmation email to your
                registered address.
              </p>
            </div>
          </div>
        </div>

        <div class="callout callout-info">
          <strong>ℹ Confirmation Email</strong>
          You will receive a cancellation confirmation email at your registered
          address. Please retain this email as proof of cancellation. If you do
          not receive a confirmation within 30 minutes, contact us at
          <a href="mailto:angularize.dev@gmail.com"
            >angularize.dev&#64;gmail.com</a
          >
          before your next renewal date.
        </div>

        <h3 class="sub-heading">3.1 Cancellation via Support</h3>
        <p>
          If you are unable to cancel through the self-service portal due to a
          verified technical issue with the billing interface, email us at
          <a href="mailto:angularize.dev@gmail.com"
            >angularize.dev&#64;gmail.com</a
          >
          with the subject line
          <strong
            >"Subscription Cancellation Request — [Your Registered
            Email]"</strong
          >. Include your account email, subscription plan, and the issue
          preventing self-service cancellation. We will process the cancellation
          within <strong>2 business days</strong> and confirm by email.
        </p>
        <p>
          Cancellation requests submitted via support are subject to the same
          no-refund rule. The support-assisted cancellation date will be the
          date we process and confirm your request — not the date you emailed
          us.
        </p>

        <h3 class="sub-heading">3.2 Timing Your Cancellation</h3>
        <p>
          To avoid being charged for the next billing period, you must cancel
          <strong>before your renewal date</strong>. Your renewal date is shown
          in your billing settings. We are not responsible for charges incurred
          because a cancellation was submitted after the renewal date.
          Auto-renewal charges that have already processed cannot be reversed —
          see Section 8.
        </p>
      </section>

      <!-- ── C4: AFTER CANCELLATION ── -->
      <section class="sec" id="c4">
        <span class="sec-num">Section 04</span>
        <h2 class="sec-heading">What Happens After Cancellation</h2>
        <p>
          Cancellation does not immediately terminate your access. The following
          applies from the moment your cancellation is confirmed:
        </p>
        <ul class="doc-list">
          <li>
            <strong>Access continues</strong> — you retain full access to all
            features of your current plan until the
            <strong>last day of the current billing period</strong>;
          </li>
          <li>
            <strong>No further charges</strong> — your subscription will not
            auto-renew at the end of the current period;
          </li>
          <li>
            <strong>Credits expire</strong> — any remaining plan-allocated
            credits expire at midnight on the last day of your billing period
            and are permanently forfeited;
          </li>
          <li>
            <strong>Access ends</strong> — at the start of the day following
            your billing period end date, your account is automatically
            downgraded to the free tier (if available) or deactivated;
          </li>
          <li>
            <strong>Projects and data</strong> — your projects and generated
            code remain accessible until your billing period ends. After that,
            access follows our data retention terms (see Section 12);
          </li>
          <li>
            <strong>No reinstatement at old rate</strong> — if you re-subscribe
            after cancellation, the pricing and plan terms in effect at that
            time apply. Previous rates, promotional pricing, or grandfathered
            terms are forfeited permanently upon cancellation.
          </li>
        </ul>
        <div class="callout callout-neutral">
          <strong>📌 Make the Most of Your Remaining Period</strong>
          After cancelling, we recommend exporting or saving your projects and
          generated code before your billing period ends, as access to
          platform-stored data may be restricted thereafter.
        </div>
      </section>

      <!-- ── C5: MONTHLY SUBSCRIPTIONS ── -->
      <section class="sec" id="c5">
        <span class="sec-num">Section 05</span>
        <h2 class="sec-heading">Monthly Subscriptions</h2>
        <p>
          Monthly subscriptions are billed on a recurring monthly basis from the
          date of your initial purchase ("<strong>billing date</strong>").
        </p>
        <ul class="doc-list">
          <li>
            If you cancel on <strong>day 1</strong> of your monthly cycle, you
            retain access for the remaining ~29 days of that month.
            <strong>No refund</strong>
            is issued for those 29 days;
          </li>
          <li>
            If you cancel on <strong>day 28</strong> of your monthly cycle, you
            retain access for the remaining ~2 days.
            <strong>No refund</strong> is issued;
          </li>
          <li>
            The subscription does not auto-renew after a confirmed cancellation;
          </li>
          <li>
            Cancelling and re-subscribing within the same billing period creates
            a new subscription at the then-current price — the original
            subscription payment is still non-refundable.
          </li>
        </ul>
        <div class="callout callout-warn">
          <strong>⚠ No Pro-Rata Refunds</strong>
          Monthly plans are charged as a whole period. There are no pro-rata
          refunds for any portion of an unused month under any circumstance.
        </div>
      </section>

      <!-- ── C6: ANNUAL SUBSCRIPTIONS ── -->
      <section class="sec" id="c6">
        <span class="sec-num">Section 06</span>
        <h2 class="sec-heading">Annual Subscriptions</h2>
        <p>
          Annual subscriptions are billed in full at the start of each 12-month
          cycle. The full annual fee is due and payable upfront as a single
          charge.
        </p>
        <ul class="doc-list">
          <li>
            If you cancel in <strong>month 1</strong> of your annual cycle, you
            retain access for the remaining 11 months.
            <strong>No refund</strong> is issued for any of those 11 months;
          </li>
          <li>
            If you cancel in <strong>month 11</strong> of your annual cycle, you
            retain access for the remaining 1 month.
            <strong>No refund</strong> is issued;
          </li>
          <li>
            The annual subscription does not auto-renew after a confirmed
            cancellation;
          </li>
          <li>
            The annual plan discount you received at the time of purchase is a
            benefit of committing to a 12-month period. Early cancellation does
            not entitle you to a refund of any portion of the annual fee, nor
            does it convert the remaining value into monthly credits or any
            other form of compensation.
          </li>
        </ul>
        <div class="callout callout-warn">
          <strong>⚠ Annual Fee Is Non-Refundable in Full</strong>
          When you purchase an annual plan, the entire annual fee is charged
          upfront and is non-refundable regardless of the date of cancellation.
          Cancelling an annual plan 1 day, 1 month, or 10 months after purchase
          yields the same result:
          <strong>zero refund</strong>.
        </div>
        <div class="callout callout-info">
          <strong>ℹ Think Before You Commit</strong>
          Annual plans offer a discounted rate in exchange for a 12-month
          commitment. If you are unsure about your long-term usage, we recommend
          starting with a monthly plan and upgrading to annual when you are
          confident in your needs.
        </div>
      </section>

      <!-- ── C7: CREDITS & ADD-ONS ── -->
      <section class="sec" id="c7">
        <span class="sec-num">Section 07</span>
        <h2 class="sec-heading">Credits &amp; Add-on Purchases</h2>
        <p>
          In addition to base subscription plans, the Platform may offer credit
          top-ups or add-on purchases. The following applies upon cancellation
          of your subscription:
        </p>
        <ul class="doc-list">
          <li>
            <strong>Plan-allocated credits</strong> (credits included with your
            subscription) expire at the end of your billing period and are
            forfeited upon cancellation. They carry no monetary value and are
            non-refundable;
          </li>
          <li>
            <strong>Purchased credit top-ups</strong> (separately purchased
            credits beyond your plan allocation) are non-refundable once
            purchased, irrespective of whether they are consumed, partially
            consumed, or entirely unused at the time of cancellation;
          </li>
          <li>
            <strong>Credits do not transfer</strong> to a new account or
            subscription. They cannot be converted to cash, carried forward
            beyond their expiry date, or transferred to another user;
          </li>
          <li>
            <strong>Add-ons</strong> (any separately purchased features,
            integrations, or capacity upgrades) are non-refundable upon
            cancellation for the same reasons as base subscriptions.
          </li>
        </ul>
        <div class="callout callout-warn">
          <strong>⚠ Credits Have No Monetary Value</strong>
          Platform credits are a unit of compute access, not a monetary
          instrument. Upon cancellation or expiry, credits are permanently
          forfeited and the Company has no obligation to compensate you for
          unused credits in any form.
        </div>
      </section>

      <!-- ── C8: AUTO-RENEWAL ── -->
      <section class="sec" id="c8">
        <span class="sec-num">Section 08</span>
        <h2 class="sec-heading">Auto-Renewal &amp; Missed Cancellations</h2>
        <p>
          All subscriptions auto-renew at the end of each billing period unless
          cancelled before the renewal date. By subscribing, you expressly
          authorise us to charge your payment method on a recurring basis.
        </p>
        <ul class="doc-list">
          <li>
            <strong>Renewal charges are final.</strong> If your subscription
            renews because you did not cancel before the renewal date, the
            renewed period is non-refundable — including if you cancel
            immediately after renewal;
          </li>
          <li>
            <strong>It is your responsibility</strong> to track your renewal
            date and cancel in advance if you do not wish to be charged for the
            next period;
          </li>
          <li>
            We send renewal reminder emails as a courtesy — failure to receive
            or read a reminder email does not constitute grounds for a refund of
            any renewal charge;
          </li>
          <li>
            If a renewal charge fails (e.g. insufficient funds or expired card),
            we may retry the charge and your access may be suspended until
            payment succeeds;
          </li>
          <li>
            Once a renewal charge has processed successfully, it is subject to
            the same absolute no-refund rule as all other subscription payments.
          </li>
        </ul>
        <div class="callout callout-neutral">
          <strong>📌 Renewal Date</strong>
          Your renewal date is always visible in your account billing settings.
          We recommend checking it before making any plans to stop using the
          Platform.
        </div>
      </section>

      <!-- ── C9: PLAN DOWNGRADES ── -->
      <section class="sec" id="c9">
        <span class="sec-num">Section 09</span>
        <h2 class="sec-heading">Plan Downgrades</h2>
        <p>
          A plan downgrade is not the same as a cancellation but is governed by
          similar no-refund principles:
        </p>
        <ul class="doc-list">
          <li>
            Downgrading from a higher-tier plan to a lower-tier plan takes
            effect at the
            <strong>start of the next billing cycle</strong>;
          </li>
          <li>
            <strong>No pro-rata refund</strong> is issued for the price
            difference between the old and new plan for the remainder of the
            current billing period;
          </li>
          <li>
            Any credits or features associated with your current higher-tier
            plan that are not available in the lower-tier plan are forfeited at
            the end of the current billing period — no compensation is provided;
          </li>
          <li>
            Downgrading from an annual plan to a monthly plan takes effect at
            the end of the current annual cycle — you cannot mid-cycle switch
            from annual to monthly billing with any form of refund for the
            unused annual period.
          </li>
        </ul>
      </section>

      <!-- ── C10: CANCELLATION BY US ── -->
      <section class="sec" id="c10">
        <span class="sec-num">Section 10</span>
        <h2 class="sec-heading">Cancellation or Termination by the Company</h2>
        <p>
          We reserve the right to suspend or terminate your subscription and
          access to the Platform at any time in accordance with our
          <a routerLink="/terms-and-conditions">Terms of Service</a>. This
          includes but is not limited to:
        </p>
        <ul class="doc-list">
          <li>
            Breach of our Terms of Service, Acceptable Use Policy, or any other
            applicable policy;
          </li>
          <li>
            Fraudulent activity, abuse of the Platform, or initiation of an
            unjustified chargeback;
          </li>
          <li>Non-payment or persistent payment failures;</li>
          <li>Legal or regulatory requirement to terminate your access;</li>
          <li>
            Our decision to discontinue the Platform or a specific plan type.
          </li>
        </ul>

        <h3 class="sub-heading">10.1 Termination for Breach — No Refund</h3>
        <p>
          If your subscription is terminated by us due to a breach of our
          policies, you are not entitled to any refund for the remaining portion
          of your billing period. Access will be terminated immediately upon
          notice (or without notice in cases of severe breach), and no
          compensation of any kind will be provided.
        </p>

        <h3 class="sub-heading">10.2 Platform Discontinuation</h3>
        <p>
          In the event that we discontinue the Platform entirely and elect to
          terminate all subscriptions, we will provide at least
          <strong>30 days' written notice</strong>
          to registered users. In this scenario only, we may, at our sole
          discretion, consider a pro-rata credit or refund for the unused
          portion of annual plans. This represents the only scenario in which
          any form of compensation upon termination by us may be considered — it
          does not create a right or entitlement.
        </p>

        <div class="callout callout-warn">
          <strong>⚠ Termination for Cause</strong>
          If your account is terminated for breach, fraud, or chargeback abuse,
          no refund or credit will be issued under any circumstances, and we
          reserve the right to pursue any outstanding amounts or damages through
          legal proceedings before the courts of Gurugram, Haryana, India.
        </div>
      </section>

      <!-- ── C11: BETA PLATFORM ── -->
      <section class="sec" id="c11">
        <span class="sec-num">Section 11</span>
        <h2 class="sec-heading">Beta Platform</h2>
        <div class="callout callout-warn">
          <strong>⚠ Beta Environment</strong>
          This section applies to subscriptions and credits used on
          <strong>beta.angularize.dev</strong>.
        </div>
        <p>
          The beta platform is a pre-release, testing environment. Any
          subscriptions or credits purchased that are consumed on or through the
          beta environment are subject to the same absolute no-refund rule as
          the production platform, with the following additional terms:
        </p>
        <ul class="doc-list">
          <li>
            The beta environment may be discontinued, reset, or merged with the
            production platform at any time without notice — this does not
            constitute grounds for any refund or credit;
          </li>
          <li>
            Features, pricing, or credit consumption rates on the beta platform
            may differ from production and are subject to change at any time;
          </li>
          <li>
            By using the beta platform with a paid subscription, you explicitly
            accept all associated risks and irrevocably waive any claim to
            compensation arising from beta-specific issues.
          </li>
        </ul>
      </section>

      <!-- ── C12: DATA AFTER CANCELLATION ── -->
      <section class="sec" id="c12">
        <span class="sec-num">Section 12</span>
        <h2 class="sec-heading">Your Data After Cancellation</h2>
        <p>
          Cancellation of your subscription affects your access to the Platform
          but does not immediately delete your data. The following applies:
        </p>
        <ul class="doc-list">
          <li>
            <strong>During the remaining billing period:</strong> All your
            projects, generated code, and account data remain fully accessible;
          </li>
          <li>
            <strong>After the billing period ends:</strong> Your account is
            downgraded or deactivated. Access to projects stored on the Platform
            may be restricted or removed;
          </li>
          <li>
            <strong>Data retention:</strong> Your personal data and content are
            retained for up to <strong>90 days</strong> after your billing
            period ends, in accordance with our
            <a routerLink="/privacy-policy">Privacy Policy</a>. After this
            window, data is permanently deleted;
          </li>
          <li>
            <strong>Export your data:</strong> We strongly recommend exporting
            all projects and generated code before your billing period ends. We
            are not liable for any loss of data after your access period
            concludes.
          </li>
        </ul>
        <div class="callout callout-neutral">
          <strong>📌 Data Export</strong>
          To export your projects, use the export feature within your account
          dashboard before your billing period ends. Once your access is
          removed, we cannot guarantee retrieval of your data.
        </div>
      </section>

      <!-- ── C13: GOVERNING LAW ── -->
      <section class="sec" id="c13">
        <span class="sec-num">Section 13</span>
        <h2 class="sec-heading">Governing Law &amp; Disputes</h2>
        <p>
          This Policy is governed by and construed in accordance with the laws
          of the
          <strong>Republic of India</strong>. Any dispute arising out of or
          relating to this Policy or any cancellation or billing matter shall be
          subject to the exclusive jurisdiction of the competent courts of
          <strong>Gurugram, Haryana, India</strong>.
        </p>
        <p>
          Before initiating formal proceedings, both parties agree to attempt
          good-faith resolution within <strong>30 days</strong> of a written
          Notice of Dispute sent to
          <a href="mailto:angularize.dev@gmail.com"
            >angularize.dev&#64;gmail.com</a
          >. For full dispute resolution terms, refer to Section 17 of our
          <a routerLink="/terms-and-conditions">Terms of Service</a>.
        </p>
      </section>

      <!-- ── C14: CHANGES ── -->
      <section class="sec" id="c14">
        <span class="sec-num">Section 14</span>
        <h2 class="sec-heading">Changes to This Policy</h2>
        <p>
          We reserve the right to amend this Cancellation Policy at any time.
          For material changes, we will update the effective date and notify
          registered users via email or a prominent notice on the Platform.
          Continued use of the Platform after changes take effect constitutes
          acceptance of the revised Policy.
        </p>
        <p>
          The current version is always available at
          <a routerLink="/cancellation-policy"
            >angularize.dev/cancellation-policy</a
          >.
        </p>
      </section>

      <!-- ── C15: CONTACT ── -->
      <section class="sec" id="c15">
        <span class="sec-num">Section 15</span>
        <h2 class="sec-heading">Contact Us</h2>
        <p>For all cancellation-related queries:</p>
        <div class="contact-card">
          <div class="contact-item">
            <span class="lbl">Company</span>
            Pythrust Technologies OPC Private Limited
          </div>
          <div class="contact-item">
            <span class="lbl">Email</span>
            <a href="mailto:angularize.dev@gmail.com"
              >angularize.dev&#64;gmail.com</a
            >
          </div>
          <div class="contact-item">
            <span class="lbl">Subject Line</span>
            Subscription Cancellation Request — [Your Email]
          </div>
          <div class="contact-item">
            <span class="lbl">Address</span>
            WeWork, 5th Floor, DLF Two Horizon Centre,<br />
            Harizan Colony, DLF Phase 5, Sector 43,<br />
            Gurugram, Haryana 122002, India
          </div>
        </div>

        <div
          class="policy-links"
          role="navigation"
          aria-label="Related policies"
        >
          <span class="policy-links-label">Related Policies</span>
          <a routerLink="/terms-and-conditions" class="policy-link-pill"
            >Terms of Service →</a
          >
          <a routerLink="/privacy-policy" class="policy-link-pill"
            >Privacy Policy →</a
          >
          <a routerLink="/refund-policy" class="policy-link-pill"
            >Refund Policy →</a
          >
        </div>
      </section>
    </main>
  </div>
  <!-- /cp-layout -->
</div>
<!-- /cp-wrap -->
